import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Spin,
  Alert,
  Typography,
  Divider,
  List,
  Avatar,
  Tag,
} from 'antd';
import {
  PieChartOutlined,
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  StockOutlined,
  LoadingOutlined,
  BankOutlined,
} from '@ant-design/icons';
import {
  getDailyHoldingsSummary,
  getDailyHoldingsByAccount,
  getAccountList,
  formatCurrency,
  formatPercentage,
} from '../services/dashboardService';

const { Title, Text } = Typography;

const PortfolioSummaryCard = ({ refreshTrigger, hideAmounts = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [accountBreakdown, setAccountBreakdown] = useState([]);
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    fetchPortfolioData();
    
    return () => {
      setIsMounted(false);
    };
  }, []);

  // refreshTrigger가 변경될 때마다 데이터 새로고침
  useEffect(() => {
    if (refreshTrigger) {
      fetchPortfolioData();
    }
  }, [refreshTrigger]);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      setError(null);

      // console.log('PortfolioSummaryCard: Fetching data...');

      // 전체 요약, 계좌별 데이터, 계좌 정보를 병렬로 가져오기
      const [summary, accountData, accountsResponse] = await Promise.all([
        getDailyHoldingsSummary().catch(err => {
          console.error('Failed to fetch daily holdings summary:', err);
          // summary 실패시 빈 객체 반환하여 다른 API는 계속 진행
          return { summaries: [] };
        }),
        getDailyHoldingsByAccount().catch(err => {
          console.error('Failed to fetch daily holdings by account:', err);
          return { results: [] };
        }),
        getAccountList().catch(err => {
          console.error('Failed to fetch account list:', err);
          return { results: [] };
        })
      ]);

      // console.log('PortfolioSummaryCard: Summary data:', summary);
      // console.log('PortfolioSummaryCard: Account data:', accountData);
      // console.log('PortfolioSummaryCard: Accounts response:', accountsResponse);

      // 계좌 정보를 ID로 매핑
      const accountsMap = {};
      if (accountsResponse && accountsResponse.results) {
        accountsResponse.results.forEach(account => {
          accountsMap[account.id] = account;
        });
      }

      // summary가 없거나 summaries가 비어있는 경우 처리
      if (!summary || !summary.summaries || summary.summaries.length === 0) {
        console.warn('PortfolioSummaryCard: No summary data available');
        // console.log('PortfolioSummaryCard: Checking if DailyHolding data exists in DB...');
        
        // 빈 데이터로 초기화
        setSummaryData({
          summaries: [],
          total_market_value: 0,
          total_cash_balance: 0,
          total_assets: 0,
          total_unrealized_pnl: 0,
          total_stocks: 0,
          total_cost: 0
        });
        setAccountBreakdown([]);
        setError('보유 현황 데이터가 없습니다. 새로고침 버튼을 눌러 데이터를 업데이트해주세요.');
        return;
      }

      setSummaryData(summary);
      
      // API 응답 구조에 맞게 데이터 처리
      if (summary && summary.summaries) {
        // 중복 제거 및 계좌별 데이터 정리
        const uniqueAccounts = [];
        const seenAccountIds = new Set();
        
        summary.summaries.forEach(account => {
          if (!seenAccountIds.has(account.account_id)) {
            seenAccountIds.add(account.account_id);
            
            // 실제 계좌 정보에서 브로커 정보 가져오기
            const accountInfo = accountsMap[account.account_id];
            const brokerCode = accountInfo?.broker || 'unknown';
            
            // 브로커 코드를 한국어 이름으로 변환
            const getBrokerDisplayName = (code) => {
              const brokerNames = {
                'kis': '한투',
                'nh': 'NH',
                'kiwoom': '키움'
              };
              return brokerNames[code?.toLowerCase()] || code || '알 수 없음';
            };
            
            uniqueAccounts.push({
              ...account,
              total_market_value: account.total_market_value || 0,
              cash_balance: account.cash_balance || 0,
              total_assets: account.total_assets || 0,
              total_unrealized_pnl: account.total_unrealized_pnl || 0,
              total_cost: account.total_buy_amount || 0,
              stock_count: account.total_holdings || 0,
              broker: getBrokerDisplayName(brokerCode)
            });
          }
        });
        
        setAccountBreakdown(uniqueAccounts);
        
        // 전체 포트폴리오 요약 계산
        const totalMarketValue = uniqueAccounts.reduce((sum, acc) => {
          const value = parseFloat(acc.total_market_value) || 0;
          return sum + value;
        }, 0);
        
        const totalCashBalance = uniqueAccounts.reduce((sum, acc) => {
          const value = parseFloat(acc.cash_balance) || 0;
          return sum + value;
        }, 0);
        
        const totalAssets = uniqueAccounts.reduce((sum, acc) => {
          const value = parseFloat(acc.total_assets) || 0;
          return sum + value;
        }, 0);
        
        const totalUnrealizedPnl = uniqueAccounts.reduce((sum, acc) => {
          const value = parseFloat(acc.total_unrealized_pnl) || 0;
          return sum + value;
        }, 0);
        
        // 중복 제거된 실제 보유 종목 수 계산
        // accountData에서 unique stock_symbol 개수 세기
        const uniqueStocks = new Set();
        if (accountData && accountData.results) {
          accountData.results.forEach(holding => {
            if (holding.stock_symbol) {
              uniqueStocks.add(holding.stock_symbol);
            }
          });
        }
        const totalStocks = uniqueStocks.size;
        
        const totalCost = uniqueAccounts.reduce((sum, acc) => {
          const value = parseFloat(acc.total_buy_amount) || 0;
          return sum + value;
        }, 0);
        
        // console.log('Portfolio summary calculation:', {
        //   totalMarketValue,
        //   totalCashBalance,
        //   totalAssets,
        //   totalUnrealizedPnl,
        //   totalStocks,
        //   totalCost
        // }); // 디버깅용
        
        setSummaryData({
          ...summary,
          total_market_value: isNaN(totalMarketValue) ? 0 : totalMarketValue,
          total_cash_balance: isNaN(totalCashBalance) ? 0 : totalCashBalance,
          total_assets: isNaN(totalAssets) ? 0 : totalAssets,
          total_unrealized_pnl: isNaN(totalUnrealizedPnl) ? 0 : totalUnrealizedPnl,
          total_stocks: isNaN(totalStocks) ? 0 : totalStocks,
          total_cost: isNaN(totalCost) ? 0 : totalCost
        });
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      setError('포트폴리오 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 계좌별 비중 계산
  const getAccountAllocation = () => {
    if (!accountBreakdown.length) return [];

    const totalValue = accountBreakdown.reduce((sum, account) => 
      sum + (account.total_market_value || 0), 0
    );

    return accountBreakdown.map(account => ({
      ...account,
      percentage: totalValue > 0 ? (account.total_market_value / totalValue) * 100 : 0
    })).sort((a, b) => b.total_market_value - a.total_market_value);
  };

  const accountAllocation = getAccountAllocation();

  // 브로커별 색상 매핑
  const getBrokerColor = (broker) => {
    const colors = {
      '한투': '#1890ff',
      'NH': '#52c41a',
      '키움': '#722ed1',
      'default': '#fa8c16'
    };
    return colors[broker] || colors.default;
  };

  // 브로커별 아이콘
  const getBrokerIcon = (broker) => {
    return <BankOutlined style={{ color: getBrokerColor(broker) }} />;
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          message="오류"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <PieChartOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <span>포트폴리오 요약</span>
        </div>
      }
      bordered={false}
      className="criclebox"
    >
      {/* 전체 포트폴리오 요약 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="총 자산"
            value={hideAmounts ? '******' : summaryData?.total_assets || 0}
            formatter={(value) => hideAmounts ? value : formatCurrency(value)}
            valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}
            prefix={<DollarOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="주식 평가액"
            value={hideAmounts ? '******' : summaryData?.total_market_value || 0}
            formatter={(value) => hideAmounts ? value : formatCurrency(value)}
            valueStyle={{ color: '#52c41a', fontSize: '18px', fontWeight: 'bold' }}
            prefix={<StockOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="총 평가손익"
            value={hideAmounts ? '******' : summaryData?.total_unrealized_pnl || 0}
            formatter={(value) => hideAmounts ? value : formatCurrency(value)}
            valueStyle={{ 
              color: (summaryData?.total_unrealized_pnl || 0) >= 0 ? '#52c41a' : '#ff4d4f',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
            prefix={(summaryData?.total_unrealized_pnl || 0) >= 0 ? <RiseOutlined /> : <FallOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="총 보유종목"
            value={summaryData?.total_stocks || 0}
            suffix="개"
            valueStyle={{ color: '#722ed1', fontSize: '18px', fontWeight: 'bold' }}
            prefix={<StockOutlined />}
          />
        </Col>
      </Row>

      <Divider />

      {/* 계좌별 분석 */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 16 }}>
          계좌별 자산 분포
        </Title>
        
        <List
          itemLayout="horizontal"
          dataSource={accountAllocation}
          renderItem={(account) => {
            const profitLossRate = account.total_cost > 0 
              ? ((account.total_unrealized_pnl || 0) / account.total_cost) * 100 
              : 0;

            return (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      icon={getBrokerIcon(account.broker)} 
                      style={{ backgroundColor: getBrokerColor(account.broker) }}
                    />
                  }
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold' }}>
                        {account.account_name}
                      </span>
                      <Tag color={getBrokerColor(account.broker)}>
                        {account.broker?.toUpperCase()}
                      </Tag>
                    </div>
                  }
                  description={
                    <div>
                      <Row gutter={[16, 8]}>
                        <Col xs={24} sm={8}>
                          <Text strong style={{ color: '#1890ff' }}>
                            {hideAmounts ? '******' : formatCurrency(account.total_market_value || 0)}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            비중: {account.percentage.toFixed(1)}%
                          </Text>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Text 
                            strong 
                            style={{ 
                              color: (account.total_unrealized_pnl || 0) >= 0 ? '#52c41a' : '#ff4d4f' 
                            }}
                          >
                            {hideAmounts ? '******' : formatCurrency(account.total_unrealized_pnl || 0)}
                          </Text>
                          <br />
                          <Text 
                            style={{ 
                              fontSize: '12px',
                              color: profitLossRate >= 0 ? '#52c41a' : '#ff4d4f' 
                            }}
                          >
                            {hideAmounts ? '***' : formatPercentage(profitLossRate)}
                          </Text>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Text strong>{account.stock_count || 0}개 종목</Text>
                          <br />
                          <Progress
                            percent={account.percentage}
                            size="small"
                            strokeColor={getBrokerColor(account.broker)}
                            showInfo={false}
                          />
                        </Col>
                      </Row>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </div>

      {/* 전체 수익률 표시 */}
      {summaryData?.total_cost > 0 && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#f0f2f5', 
            borderRadius: '8px',
            border: '1px solid #d9d9d9'
          }}>
            <Text type="secondary">전체 포트폴리오 수익률</Text>
            <br />
            <Text 
              style={{ 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: (summaryData.total_unrealized_pnl || 0) >= 0 ? '#52c41a' : '#ff4d4f'
              }}
            >
              {hideAmounts ? '******' : formatPercentage(
                ((summaryData.total_unrealized_pnl || 0) / summaryData.total_cost) * 100
              )}
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
};

export default PortfolioSummaryCard;
